/**
 * GeodreieckOverlay – vektorbasiertes Geodreieck (90°-45°-45°) für Display und Mirror.
 * Skalierbar, drehbar, transparenter Hintergrund. Halbkreis 0–180°, Längenskala an Hypotenuse (2 Kästchen = 1).
 */
import { useRef, useState, useCallback } from 'react';

const VIEWBOX_SIZE = 200; // logical units for SVG
/** Rechenkästchen-Seitenlänge in Display-Pixeln (wie in Display.tsx showMathGrid) */
const GRID_CELL_PX = 40;
/** Längenskala: 2 Kästchen = 1 Einheit → 1 Einheit = 80 Display-Pixel */
const LENGTH_UNIT_PX = 2 * GRID_CELL_PX;

const SCALE_MIN = 0.4;
const SCALE_MAX = 6;

export interface GeodreieckState {
  visible: boolean;
  x: number;
  y: number;
  rotation: number; // degrees
  scale: number;
}

export const DEFAULT_GEODREIECK: GeodreieckState = {
  visible: false,
  x: 220,
  y: 980,
  rotation: 0,
  scale: 1,
};

interface GeodreieckOverlayProps {
  /** Position and transform in Display coordinates (0..CANVAS_W, 0..CANVAS_H) */
  x: number;
  y: number;
  rotation: number;
  scale: number;
  /** Scale factor of the canvas container (transform scale) – used to convert pointer deltas to Display coords */
  displayScale?: number;
  /** Whether user can drag/rotate the overlay */
  interactive?: boolean;
  onMove?: (x: number, y: number) => void;
  onRotate?: (rotation: number) => void;
  onScale?: (scale: number) => void;
  /** Optional: Magnet-Snap für Dreh-/Skalier-Handle (Rechenkaro-Kreuzungen). */
  snapToGrid?: (x: number, y: number) => { x: number; y: number };
  /** Stroke color (e.g. for contrast on dark/light background) */
  strokeColor?: string;
}

export function GeodreieckOverlay({
  x,
  y,
  rotation,
  scale,
  displayScale = 1,
  interactive = true,
  onMove,
  onRotate,
  onScale,
  snapToGrid,
  strokeColor = 'rgba(255,255,255,0.85)',
}: GeodreieckOverlayProps) {
  const size = VIEWBOX_SIZE * scale;
  const containerRef = useRef<HTMLDivElement>(null);

  // Hypotenuse-Längenskala: 2 Kästchen = 1 → Schritt in ViewBox-Einheiten = 80/scale
  const lengthStepViewBox = scale > 0 ? LENGTH_UNIT_PX / scale : 80;
  const semicircleRadius = 72;
  const semicircleCenter = VIEWBOX_SIZE / 2;
  const [dragMode, setDragMode] = useState<'move' | 'rotateScale' | null>(null);
  const dragStartRef = useRef<{
    clientX: number;
    clientY: number;
    x: number;
    y: number;
    rotation: number;
    scale: number;
    centerClientX?: number;
    centerClientY?: number;
    angleRad?: number;
    initialDistance?: number;
  } | null>(null);

  /** Aktive Pointer (Maus oder Finger) – ein Pointer = Mausbedienung, zwei = Multitouch */
  const activePointersRef = useRef<Map<number, { clientX: number; clientY: number }>>(new Map());
  const multitouchStartRef = useRef<{
    centerClientX: number;
    centerClientY: number;
    distance: number;
    angleRad: number;
    x: number;
    y: number;
    rotation: number;
    scale: number;
  } | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, mode: 'move' | 'rotateScale') => {
      if (!interactive || (mode === 'move' && !onMove) || (mode === 'rotateScale' && !onRotate && !onScale)) return;
      e.preventDefault();
      e.stopPropagation();
      const map = activePointersRef.current;
      map.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });

      if (map.size >= 2) {
        setDragMode(null);
        dragStartRef.current = null;
        try {
          map.forEach((_, id) => containerRef.current?.releasePointerCapture(id));
        } catch { /* best-effort */ }
        const [[, a], [, b]] = Array.from(map.entries());
        const centerClientX = (a.clientX + b.clientX) / 2;
        const centerClientY = (a.clientY + b.clientY) / 2;
        const distance = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY) || 1;
        const angleRad = Math.atan2(b.clientY - a.clientY, b.clientX - a.clientX);
        multitouchStartRef.current = {
          centerClientX,
          centerClientY,
          distance,
          angleRad,
          x,
          y,
          rotation,
          scale,
        };
        return;
      }

      setDragMode(mode);
      const rect = containerRef.current?.getBoundingClientRect();
      const centerClientX = rect ? rect.left + rect.width / 2 : e.clientX;
      const centerClientY = rect ? rect.top + rect.height / 2 : e.clientY;
      const angleRad = Math.atan2(e.clientY - centerClientY, e.clientX - centerClientX);
      const distance = Math.hypot(e.clientX - centerClientX, e.clientY - centerClientY) || 1;
      dragStartRef.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        x,
        y,
        rotation,
        scale,
        centerClientX,
        centerClientY,
        angleRad,
        initialDistance: distance,
      };
      try {
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      } catch { /* best-effort */ }
    },
    [interactive, x, y, rotation, scale, onMove, onRotate, onScale]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const map = activePointersRef.current;
      map.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });

      if (map.size === 2) {
        const start = multitouchStartRef.current;
        if (!start || (!onMove && !onRotate && !onScale)) return;
        const [[, a], [, b]] = Array.from(map.entries());
        const centerClientX = (a.clientX + b.clientX) / 2;
        const centerClientY = (a.clientY + b.clientY) / 2;
        const distance = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY) || 1;
        const angleRad = Math.atan2(b.clientY - a.clientY, b.clientX - a.clientX);
        if (onMove) {
          const dx = (centerClientX - start.centerClientX) / displayScale;
          const dy = (centerClientY - start.centerClientY) / displayScale;
          onMove(start.x + dx, start.y + dy);
        }
        if (onRotate && distance > 0) {
          const deltaDeg = ((angleRad - start.angleRad) * 180) / Math.PI;
          onRotate(start.rotation + deltaDeg);
        }
        if (onScale && start.distance > 0) {
          const factor = distance / start.distance;
          onScale(Math.max(SCALE_MIN, Math.min(SCALE_MAX, start.scale * factor)));
        }
        multitouchStartRef.current = {
          ...start,
          centerClientX,
          centerClientY,
          distance,
          angleRad,
          x: start.x + (centerClientX - start.centerClientX) / displayScale,
          y: start.y + (centerClientY - start.centerClientY) / displayScale,
          rotation: start.rotation + ((angleRad - start.angleRad) * 180) / Math.PI,
          scale: start.distance > 0 ? Math.max(SCALE_MIN, Math.min(SCALE_MAX, start.scale * (distance / start.distance))) : start.scale,
        };
        return;
      }

      const start = dragStartRef.current;
      if (!start || dragMode === null) return;
      if (dragMode === 'move' && onMove) {
        const dx = (e.clientX - start.clientX) / displayScale;
        const dy = (e.clientY - start.clientY) / displayScale;
        onMove(start.x + dx, start.y + dy);
      } else if (dragMode === 'rotateScale' && (onRotate || onScale)) {
        const rect = containerRef.current?.getBoundingClientRect();
        const centerClientX = rect ? rect.left + rect.width / 2 : e.clientX;
        const centerClientY = rect ? rect.top + rect.height / 2 : e.clientY;
        let handleX = start.x + (e.clientX - centerClientX) / displayScale;
        let handleY = start.y + (e.clientY - centerClientY) / displayScale;
        if (snapToGrid) {
          const sn = snapToGrid(handleX, handleY);
          handleX = sn.x;
          handleY = sn.y;
        }
        if (onRotate && start.centerClientX != null && start.centerClientY != null && start.angleRad != null) {
          const newAngleRad = Math.atan2(handleY - start.y, handleX - start.x);
          const deltaDeg = ((newAngleRad - start.angleRad) * 180) / Math.PI;
          const nextRotation = start.rotation + deltaDeg;
          onRotate(nextRotation);
          dragStartRef.current = { ...start, rotation: nextRotation, angleRad: newAngleRad };
        }
        if (onScale && start.initialDistance != null && start.initialDistance > 0) {
          const distDisplay = Math.hypot(handleX - start.x, handleY - start.y) || 1;
          const nextScale = Math.max(SCALE_MIN, Math.min(SCALE_MAX, (distDisplay * displayScale * start.scale) / start.initialDistance));
          onScale(nextScale);
        }
      }
    },
    [dragMode, onMove, onRotate, onScale, snapToGrid, displayScale]
  );

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    activePointersRef.current.delete(e.pointerId);
    const map = activePointersRef.current;
    if (map.size === 0) {
      setDragMode(null);
      dragStartRef.current = null;
      multitouchStartRef.current = null;
    } else if (map.size === 1) {
      multitouchStartRef.current = null;
      setDragMode(null);
      dragStartRef.current = null;
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute z-[18] touch-none select-none"
      style={{
        left: 0,
        top: 0,
        width: size,
        height: size,
        transform: `translate(${x - size / 2}px, ${y - size / 2}px) rotate(${rotation}deg)`,
        transformOrigin: '50% 50%',
        pointerEvents: interactive ? 'auto' : 'none',
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      {/* SVG: right-angled triangle, Halbkreis 0–180°, Längenskala an Hypotenuse */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
        className="block"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <filter id="geodreieck-outline" x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0" dy="0" stdDeviation="0.5" floodColor="#000" floodOpacity="0.3" />
          </filter>
        </defs>
        <g opacity={0.5}>
        {/* Dreieck: rechter Winkel bei (0,0), Katheten entlang x und y */}
        <path
          d={`M 0 0 L ${VIEWBOX_SIZE} 0 L 0 ${VIEWBOX_SIZE} Z`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={1.2}
          filter="url(#geodreieck-outline)"
        />
        {/* Transparenter gelber Halbkreis im Inneren: 0–180°; Durchmesser auf Hypotenuse, Bogen zum rechten Winkel */}
        <path
          d={`M ${semicircleCenter + (semicircleRadius / Math.SQRT2)} ${semicircleCenter - (semicircleRadius / Math.SQRT2)} A ${semicircleRadius} ${semicircleRadius} 0 0 0 ${semicircleCenter - (semicircleRadius / Math.SQRT2)} ${semicircleCenter + (semicircleRadius / Math.SQRT2)} Z`}
          fill="rgba(255, 235, 0, 0.25)"
          stroke="none"
        />
        {/* Halbkreis-Bogen (ohne Fläche zum Rechtwinkeleck) als saubere Linie */}
        <path
          d={`M ${semicircleCenter + (semicircleRadius / Math.SQRT2)} ${semicircleCenter - (semicircleRadius / Math.SQRT2)} A ${semicircleRadius} ${semicircleRadius} 0 0 0 ${semicircleCenter - (semicircleRadius / Math.SQRT2)} ${semicircleCenter + (semicircleRadius / Math.SQRT2)}`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={1}
        />
        {/* Skala auf dem Halbkreis: Striche alle 5° (radial vom Mittelpunkt) */}
        {Array.from({ length: 37 }, (_, i) => {
          const deg = i * 5;
          const angleDeg = 315 - deg;
          const rad = (angleDeg * Math.PI) / 180;
          const cos = Math.cos(rad);
          const sin = Math.sin(rad);
          const tickLen = deg % 10 === 0 ? 4 : 2;
          const r1 = semicircleRadius - tickLen;
          const r2 = semicircleRadius;
          return (
            <line
              key={`tick-${deg}`}
              x1={semicircleCenter + r1 * cos}
              y1={semicircleCenter + r1 * sin}
              x2={semicircleCenter + r2 * cos}
              y2={semicircleCenter + r2 * sin}
              stroke={strokeColor}
              strokeWidth={deg % 10 === 0 ? 0.9 : 0.6}
            />
          );
        })}
        {/* Gradbeschriftung 0–180: Skalenteilung abhängig von Größe (bei max gut lesbar = 10°; kleiner → gröbere Teilung) */}
        {(() => {
          const stepDeg = scale >= 2 ? 10 : scale >= 1.2 ? 15 : scale >= 0.8 ? 20 : scale >= 0.55 ? 30 : 45;
          const count = Math.floor(180 / stepDeg) + 1;
          return Array.from({ length: count }, (_, i) => {
            const deg = i * stepDeg;
            if (deg > 180) return null;
            const angleDeg = 315 - deg;
            const rad = (angleDeg * Math.PI) / 180;
            const r = semicircleRadius - 8;
            const tx = semicircleCenter + r * Math.cos(rad);
            const ty = semicircleCenter + r * Math.sin(rad);
            return (
              <text
                key={deg}
                x={tx}
                y={ty}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={strokeColor}
                fontSize={deg % 90 === 0 ? 7 : 5}
                fontWeight={deg % 90 === 0 ? 'bold' : 'normal'}
              >
                {deg}
              </text>
            );
          });
        })()}
        {/* Längenskala an der Hypotenuse: 2 Kästchen = 1; Mittelpunkt = 0, dann ±1, ±2, … */}
        {(() => {
          const hypHalf = (VIEWBOX_SIZE * Math.SQRT2) / 2;
          const step = lengthStepViewBox;
          const maxN = Math.floor(hypHalf / step);
          const dirX = 1 / Math.SQRT2;
          const dirY = -1 / Math.SQRT2;
          const ticks: { d: number; n: number }[] = [];
          for (let n = -maxN; n <= maxN; n++) {
            const d = n * step;
            if (Math.abs(d) <= hypHalf + 1) ticks.push({ d, n });
          }
          return (
            <>
              {ticks.map(({ d, n }) => {
                const px = semicircleCenter + d * dirX;
                const py = semicircleCenter + d * dirY;
                const perpX = -dirY;
                const perpY = dirX;
                const tickLen = n % 2 === 0 ? 6 : 4;
                return (
                  <g key={n}>
                    <line
                      x1={px}
                      y1={py}
                      x2={px + perpX * tickLen}
                      y2={py + perpY * tickLen}
                      stroke={strokeColor}
                      strokeWidth={0.8}
                    />
                    <text
                        x={px + perpX * 10}
                        y={py + perpY * 10}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill={strokeColor}
                        fontSize={n === 0 ? 6 : 5}
                        fontWeight={n === 0 ? 'bold' : 'normal'}
                      >
                        {n === 0 ? '0' : Math.abs(n)}
                      </text>
                  </g>
                );
              })}
            </>
          );
        })()}
        {/* Katheten-Skala: Projektion der Winkel des inneren Bogens (0°–180°), Striche alle 5°, Beschriftung 10°, 20°, … */}
        {Array.from({ length: 37 }, (_, i) => {
          const deg = i * 5;
          const angleRad = ((315 - deg) * Math.PI) / 180;
          const cos = Math.cos(angleRad);
          const sin = Math.sin(angleRad);
          let px = 0;
          let py = 0;
          let tickDx = 0;
          let tickDy = 0;
          let labelX = 0;
          let labelY = 0;
          if (deg < 90) {
            const t = -100 / sin;
            px = 100 + t * cos;
            py = 0;
            if (px < 0 || px > VIEWBOX_SIZE) return null;
            tickDx = 0;
            tickDy = deg % 10 === 0 ? 5 : 3;
            labelX = px;
            labelY = 8;
          } else if (deg > 90) {
            const t = -100 / cos;
            px = 0;
            py = 100 + t * sin;
            if (py < 0 || py > VIEWBOX_SIZE) return null;
            tickDx = deg % 10 === 0 ? 5 : 3;
            tickDy = 0;
            labelX = 8;
            labelY = py;
          } else {
            return null;
          }
          return (
            <g key={`leg-${deg}`}>
              <line
                x1={px}
                y1={py}
                x2={px + tickDx}
                y2={py + tickDy}
                stroke={strokeColor}
                strokeWidth={deg % 10 === 0 ? 0.7 : 0.5}
              />
              {deg % 10 === 0 && deg !== 0 && deg !== 180 && (
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor={deg < 90 ? 'middle' : 'start'}
                  dominantBaseline={deg < 90 ? 'hanging' : 'middle'}
                  fill={strokeColor}
                  fontSize={5}
                >
                  {180 - deg}
                </text>
              )}
            </g>
          );
        })}
        {/* 90°-Bogen am rechten Winkel */}
        <path
          d="M 10 0 A 10 10 0 0 1 7.07 7.07"
          fill="none"
          stroke={strokeColor}
          strokeWidth={0.8}
        />
        </g>
      </svg>
      {/* Invisible larger hit area for move; rotate handle at tip */}
      {interactive && (
        <>
          <div
            className="absolute inset-0 cursor-move"
            style={{ left: 0, top: 0, width: size, height: size }}
            onPointerDown={(e) => handlePointerDown(e, 'move')}
            aria-hidden
          />
          <div
            className="absolute cursor-grab active:cursor-grabbing rounded-full border-2 border-white/60 bg-white/20"
            style={{
              left: size - 4,
              top: -4,
              width: 24,
              height: 24,
              transform: 'translate(-50%, -50%)',
            }}
            onPointerDown={(e) => handlePointerDown(e, 'rotateScale')}
            title="Drehen & Größe (Abstand zum Drehpunkt)"
            aria-label="Geodreieck drehen und skalieren"
          />
        </>
      )}
    </div>
  );
}
